// =============================================================================
// components/ui/FreshnessIndicator.tsx — "Atualizado às HH:MM" + refresh manual.
//
// Diagnóstico D4: o dado do dashboard estava fresco, mas o frescor era invisível
// — cache de 3 min no cliente somado ao tick de 5 min do worker de MVs, sem
// timestamp, sem botão de atualizar e sem sinal de revalidação. Quem ficava com
// a tela aberta concluía, com razão, que "o dashboard não atualiza".
//
// Dois relógios distintos, deliberadamente:
//   `updatedAt`   — quando ESTA TELA recebeu os números.
//   `refreshedAt` — quando o DADO por trás foi recalculado (refresh das MVs).
// Em /relatorios os dois podem estar minutos separados; mostrar só o primeiro
// esconderia justamente o atraso que a operação sente.
//
// Sem PII.
// =============================================================================

import * as React from 'react';

interface FreshnessIndicatorProps {
  /** Epoch ms da última resposta recebida pela tela. 0 = ainda não chegou. */
  updatedAt: number;
  /** Revalidando em background — mostra o ponto pulsante. */
  isFetching: boolean;
  /** Dispara a revalidação manual. */
  onRefresh: () => void;
  /**
   * Instante do último recálculo do dado de origem (ISO 8601), quando aplicável.
   * `null`/ausente = não há heartbeat a exibir.
   */
  refreshedAt?: string | null;
  /** Fuso em que os horários são exibidos. Default: fuso do navegador. */
  timeZone?: string | undefined;
}

function formatClock(date: Date, timeZone?: string | undefined): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone !== undefined ? { timeZone } : {}),
  }).format(date);
}

/**
 * Selo de frescor com ação de atualizar.
 *
 * O horário é reavaliado a cada 30 s para que "atualizado às 09:12" não fique
 * congelado numa tela que passou a manhã aberta.
 */
export function FreshnessIndicator({
  updatedAt,
  isFetching,
  onRefresh,
  refreshedAt = null,
  timeZone,
}: FreshnessIndicatorProps): React.JSX.Element {
  const [, forceTick] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    const id = window.setInterval(() => forceTick(), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const updatedLabel = updatedAt > 0 ? formatClock(new Date(updatedAt), timeZone) : null;
  const dataLabel =
    refreshedAt !== null && refreshedAt !== undefined
      ? formatClock(new Date(refreshedAt), timeZone)
      : null;

  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono inline-flex items-center gap-1.5"
        style={{
          fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
          fontSize: '0.7rem',
          color: 'var(--text-4)',
        }}
        title={
          dataLabel !== null
            ? `Tela atualizada às ${updatedLabel ?? '—'}; dados recalculados às ${dataLabel}.`
            : undefined
        }
      >
        <span
          aria-hidden="true"
          className={`inline-block rounded-pill${isFetching ? ' animate-pulse' : ''}`}
          style={{
            width: '6px',
            height: '6px',
            background: isFetching ? 'var(--brand-azul)' : 'var(--success)',
          }}
        />
        {updatedLabel !== null ? (
          <>
            Atualizado às {updatedLabel}
            {dataLabel !== null && dataLabel !== updatedLabel ? (
              <span className="opacity-70"> · dados de {dataLabel}</span>
            ) : null}
          </>
        ) : (
          'Carregando…'
        )}
      </span>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isFetching}
        aria-label="Atualizar os números agora"
        className="font-sans text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xs border transition-all duration-fast hover:opacity-80 focus:outline-none focus-visible:ring-2 active:scale-95 disabled:opacity-50 disabled:active:scale-100"
        style={{
          borderColor: 'var(--border-strong)',
          color: 'var(--text-3)',
          background: 'var(--bg-elev-1)',
          ['--tw-ring-color' as string]: 'rgba(27,58,140,0.15)',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={isFetching ? 'animate-spin' : undefined}
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
        Atualizar
      </button>
    </div>
  );
}
